import { useEffect } from 'react';
import { useAppContext } from '../../Context';
import { AttributesType, ColumnsType, OrderType } from '../../types';

import { useNavigate } from 'react-router-dom';

import DataTable from '../../components/shared/DataTable';

// hooks redux
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../hooks';
import { setOrder, setOrdersToValidate } from '../../slices/ordersSlice';
import { setLoading, setPopConfirmation } from '../../slices/miscSlice';

import { printOrderTable, useGroups, usePermissions } from '../../services';
import { Button } from '../../components/shared/FormElements';
import { Grid } from '@mui/material';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faTrash, faPrint, faPlay, faRotateBack, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { constants } from '../../constants';

const ViewOrdersUnvalidated: React.FC = () => {
  const { apiCall } = useAppContext();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const ordersToValidate = useSelector((state: any) => state.orders.ordersToValidate);

  const {
    canView_orders,
    canView_orders_all,
    canView_orders_own,
    canView_menu_orders_to_validate,
    canAdd_orders,
    canDelete_orders,
    canAdd_orderprocess,
    canValidate_orders,
  } = usePermissions([
    'view_orders',
    'view_orders_all',
    'view_orders_own',
    'view_menu_orders_to_validate',
    'add_orders',
    'change_orders',
    'delete_orders',
    'add_orderprocess',
    'validate_orders',
  ]);
  const { isValidador, isAdministrador } = useGroups([
    constants.groups.GROUP_VALIDADOR,
    constants.groups.GROUP_ADMINISTRADOR,
  ]);

  useEffect(
    () => {
      const getOrders = async () => {
        const response = await apiCall('GET', 'orders/unvalidated/', null, '', 'Error al obtener las ordenes');
        if (response) {
          dispatch(setOrdersToValidate(response));
        }
      };

      getOrders();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  if (!canValidate_orders && !canView_menu_orders_to_validate) {
    navigate('/');
    return null;
  }

  const hoyOnlyDate = new Date();
  hoyOnlyDate.setHours(0, 0, 0, 0);

  const conditionDecorator = {
    operator: '<',
    value: hoyOnlyDate,
    class_decorator: 'text-danger',
    type: 'date',
    limit: constants.settings.limitDateDays,
  };

  const decoratorEstado = {
    Finalizado: 'success',
    Bloqueado: 'warning',
    Creada: 'info',
    Cancelada: 'error',
  };
  const columns: Array<ColumnsType> = [
    { title: 'ID', field: 'id' },
    { title: 'Cliente', field: 'customer_name' },
    { title: 'Línea', field: 'line_name' },
    { title: 'Cons', field: 'consecutives_count', cell_class: 'text-center' },
    { title: 'Vendedor', field: 'vendedor', cell_class: 'text-center' },
    { title: 'Fecha', field: 'order_date', cell_class: 'text-center' },
    { title: 'Inicio Prod.', field: 'production_date_start', cell_class: 'text-center' },
    {
      title: 'Fin Prod.',
      field: 'production_date_end',
      type: 'conditional_decorator',
      decorator: conditionDecorator,
      cell_class: 'text-center',
    },
    {
      title: 'Entrega',
      field: 'delivery_date',
      type: 'conditional_decorator',
      decorator: conditionDecorator,
      cell_class: 'text-center',
    },
    {
      title: 'Estado Orden',
      field: 'status',
      type: 'decorator',
      decorator: decoratorEstado,
      cell_class: 'text-center',
    },
  ];

  const handleViewOrder = (order: OrderType) => {
    dispatch(setOrder(order));
    navigate(`/orden/${order.id}`);
  };

  const handleDeleteOrder = (order: OrderType) => {
    dispatch(
      setPopConfirmation({
        show: true,
        message: '¿Está seguro que desea eliminar la orden?',
        alertType: 'danger',
        okFunc: () => deleteOrder(order.id),
      })
    );
  };
  const deleteOrder = async (order_id: number) => {
    const response = await apiCall('DELETE', `order/${order_id}/`, null, '', 'Error al eliminar la orden');
    if (response) {
      dispatch(setOrdersToValidate(ordersToValidate.filter((o: OrderType) => o.id !== order_id)));
    }
  };

  const handlePrintOrder = async (order: OrderType) => {
    let boceto = null;
    const resp = await apiCall('GET', `order/${order.id}/boceto/`, null, '', 'Error al obtener la imagen');
    if (resp?.image) {
      boceto = resp.image;
    }
    dispatch(setLoading(true));
    await printOrderTable(order, boceto);
    dispatch(setLoading(false));
  };

  const handleAddProcess = (order: OrderType) => {
    dispatch(
      setPopConfirmation({
        show: true,
        message: '¿Está seguro que desea enviar la orden a producción?',
        alertType: 'danger',
        okFunc: () => addProcess(order.id),
      })
    );
  };
  const addProcess = async (order_id: number) => {
    const response = await apiCall(
      'POST',
      `order/${order_id}/add_process/`,
      null,
      '',
      'Error al enviar la orden a producción'
    );
    if (response) {
      dispatch(setOrdersToValidate(ordersToValidate.filter((o: OrderType) => o.id !== order_id)));
    }
  };

  const handleAddProcessOrderOnly = (order: OrderType) => {
    dispatch(
      setPopConfirmation({
        show: true,
        message: '¿Está seguro que desea enviar la orden a producción sin consecutivos?',
        alertType: 'danger',
        okFunc: () => addProcessOrderOnly(order.id),
      })
    );
  };

  const addProcessOrderOnly = async (order_id: number) => {
    const response = await apiCall(
      'POST',
      `order/${order_id}/add_process/`,
      { only_order: true },
      '',
      'Error al enviar la orden a producción'
    );
    if (response) {
      dispatch(setOrdersToValidate(ordersToValidate.filter((o: OrderType) => o.id !== order_id)));
    }
  };

  const handleReject = (order: OrderType) => {
    dispatch(
      setPopConfirmation({
        show: true,
        message: '¿Está seguro que desea devolver la orden a Creadas?',
        alertType: 'danger',
        okFunc: () => rejectOrder(order.id),
      })
    );
  };

  const rejectOrder = async (order_id: number) => {
    const response = await apiCall(
      'PUT',
      `order/${order_id}/created/`,
      null,
      '',
      'Error al devolver la orden a Creadas'
    );
    if (response) {
      dispatch(setOrdersToValidate(ordersToValidate.filter((o: OrderType) => o.id !== order_id)));
    }
  };

  const ableToReject = isValidador || isAdministrador;

  const canViewOrderDetail =
    canView_orders || canView_orders_all || canView_orders_own || canView_menu_orders_to_validate;

  const actions: Array<any> = [
    canViewOrderDetail
      ? {
          icon: <FontAwesomeIcon icon={faEye} />,
          tooltip: 'Ver',
          onClick: (rowData: OrderType) => handleViewOrder(rowData),
        }
      : null,
    canDelete_orders
      ? {
          icon: <FontAwesomeIcon icon={faTrash} />,
          tooltip: 'Eliminar',
          onClick: (rowData: any) => handleDeleteOrder(rowData),
        }
      : null,
    canView_orders
      ? {
          icon: <FontAwesomeIcon icon={faPrint} />,
          tooltip: 'Imprimir',
          onClick: (rowData: any) => handlePrintOrder(rowData),
        }
      : null,
    canAdd_orderprocess
      ? {
          icon: <FontAwesomeIcon icon={faPlay} />,
          tooltip: 'Enviar a producción',
          onClick: (rowData: any) => handleAddProcess(rowData),
          condition: { field: 'status', value: constants.orderStatus.POR_VALIDAR, operator: '===' },
        }
      : null,
    canAdd_orderprocess
      ? {
          icon: <FontAwesomeIcon icon={faPaperPlane} />,
          tooltip: 'Enviar solo orden a producción(no consecutivos)',
          onClick: (rowData: any) => handleAddProcessOrderOnly(rowData),
          condition: { field: 'consecutives_count', value: 0, operator: '>' },
        }
      : null,
    ableToReject
      ? {
          icon: <FontAwesomeIcon icon={faRotateBack} />,
          tooltip: 'Devolver a creación',
          onClick: (rowData: any) => handleReject(rowData),
          condition: { field: 'status', value: constants.orderStatus.POR_VALIDAR, operator: '===' },
        }
      : null,
  ];

  const filterDate: Array<AttributesType> = [
    { value: 'order_date', name: 'Fecha de Creación' },
    { value: 'production_date_start', name: 'Fecha de Inicio de Produccion' },
    { value: 'production_date_end', name: 'Fecha de Fin de Produccion' },
    { value: 'delivery_date', name: 'Fecha de Entrega' },
  ];

  return (
    <div className='container-wrap'>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={10} className='text-left'>
          <h2 className='text-left'>Órdenes Por Validar</h2>
        </Grid>
        <Grid item xs={12} sm={2} className='text-right'>
          {canAdd_orders && (
            <Button className='btn btn-primary' onClick={() => navigate('/orden/crear')}>
              Agregar
            </Button>
          )}
        </Grid>
      </Grid>
      <DataTable
        data={ordersToValidate}
        columns={columns}
        actions={actions}
        groupedOptionBy={'current_process_name'}
        filterByDateBy={filterDate}
      />
    </div>
  );
};

export default ViewOrdersUnvalidated;
